import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';

/**
 * Account-type intent passed to the modal at open time. Lets buyer-flavored
 * surfaces ("Continue to shop", chat-panel sign-in nudge, future product
 * checkout gates, …) preselect the buyer signup branch without each call
 * site having to reach into the modal's internal state.
 *
 * Read by `AuthModal` via the `signupIntent` field on this context.
 */
export type AuthModalAccountIntent = 'creator' | 'individual' | 'buyer';

interface OpenOptions {
  /** Preselect the signup account-type radio. Only used when opening the
   *  signup mode (or when the user switches to it from login). */
  accountType?: AuthModalAccountIntent;
}

interface AuthModalContextType {
  showAuthModal: boolean;
  authModalMode: 'login' | 'signup' | 'forgot-password';
  /** Last-set signup intent. `AuthModal` reads this to preselect the
   *  account-type radio when opening / switching to signup. Cleared on
   *  close so a stale intent doesn't leak into the next open. */
  signupIntent: AuthModalAccountIntent | null;
  openLoginModal: (opts?: OpenOptions) => void;
  openSignupModal: (opts?: OpenOptions) => void;
  openForgotPasswordModal: () => void;
  closeAuthModal: () => void;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

/**
 * Routes that are themselves dedicated auth surfaces. The login/signup modal
 * must never appear on these — it would either cover the actual auth UI
 * (e.g. /auth/confirm asking the user to verify email) or create a confusing
 * "log in to log in" loop. Anyone calling `openLoginModal()` from a stale
 * handler while we're on one of these routes is silently dropped.
 */
const STANDALONE_AUTH_PATHS = [
  '/auth/confirm',
  '/auth/callback',
  '/reset-password',
  '/login',
  '/signup',
  '/forgot-password',
];

const isStandaloneAuthPath = (pathname: string) =>
  STANDALONE_AUTH_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );

export const AuthModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'signup' | 'forgot-password'>('login');
  const [signupIntent, setSignupIntent] = useState<AuthModalAccountIntent | null>(null);

  const onAuthRoute = isStandaloneAuthPath(location.pathname);

  // Mirror Redux auth + route state into refs so the imperative
  // `openLoginModal` / `openSignupModal` / `openForgotPasswordModal`
  // callbacks always see the *latest* values — not the closure snapshot
  // from the render where the function was created. Without this, a
  // caller that fires `dispatch(login.fulfilled)` and `openLoginModal()`
  // on the same tick (or any handler that opens the modal *after* login
  // resolves but before the provider has re-rendered) reads a stale
  // `isAuthenticated=false` and the modal pops back open. That's the
  // "logged in, then bounced back to the login modal until I refresh"
  // regression.
  const isAuthenticatedRef = useRef(isAuthenticated);
  const onAuthRouteRef = useRef(onAuthRoute);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);
  useEffect(() => {
    onAuthRouteRef.current = onAuthRoute;
  }, [onAuthRoute]);

  // Defensive: whenever the auth slice says we're authenticated, force-close
  // the modal at the source. We watch `showAuthModal` in addition to
  // `isAuthenticated` so the effect re-runs if *any* later caller (stale
  // handler, async race, etc.) flips the modal back open after the initial
  // login transition — without this, the effect only fires once on the
  // false→true edge and a re-open afterwards stays stuck until reload.
  useEffect(() => {
    if (isAuthenticated && showAuthModal) {
      setShowAuthModal(false);
      setSignupIntent(null);
    }
  }, [isAuthenticated, showAuthModal]);

  // Force-close the modal when navigation lands on a standalone auth page.
  // This catches the race where a setTimeout/promise that opened the modal
  // resolves *after* we've already navigated to /auth/confirm or similar.
  useEffect(() => {
    if (onAuthRoute) {
      setShowAuthModal(false);
      setSignupIntent(null);
    }
  }, [onAuthRoute]);

  const openLoginModal = (opts?: OpenOptions) => {
    // Hard-stop auth modal opens once Redux says the user is signed in.
    // We read through a ref so same-tick callers (e.g. a handler that
    // dispatches `login.fulfilled` and then opens the modal before the
    // provider has had a chance to re-render with the new isAuthenticated
    // value) still see the latest auth state and can't sneak past the
    // guard with a stale closure.
    if (isAuthenticatedRef.current) return;
    // Same idea on dedicated auth routes — don't paint a modal over the
    // page that *is* the auth surface.
    if (onAuthRouteRef.current) return;
    // Carry the intent even when opening login — if the user flips to
    // signup from inside the modal, the right radio is already selected.
    if (opts?.accountType) setSignupIntent(opts.accountType);
    setAuthModalMode('login');
    setShowAuthModal(true);
  };

  const openSignupModal = (opts?: OpenOptions) => {
    if (isAuthenticatedRef.current) return;
    if (onAuthRouteRef.current) return;
    if (opts?.accountType) setSignupIntent(opts.accountType);
    setAuthModalMode('signup');
    setShowAuthModal(true);
  };

  const openForgotPasswordModal = () => {
    if (isAuthenticatedRef.current) return;
    if (onAuthRouteRef.current) return;
    setAuthModalMode('forgot-password');
    setShowAuthModal(true);
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
    setSignupIntent(null);
  };

  return (
    <AuthModalContext.Provider
      value={{
        showAuthModal,
        authModalMode,
        signupIntent,
        openLoginModal,
        openSignupModal,
        openForgotPasswordModal,
        closeAuthModal,
      }}
    >
      {children}
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = () => {
  const context = useContext(AuthModalContext);
  if (context === undefined) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return context;
};
